import { BulkProjectTypeEnum } from "@app/targeting/import/interfaces";
import { ExportColumn } from "@app/report-customization/build-reports/interfaces/exportColumns";
import ExportService from "@app/exports/exportService";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";

export interface TargetingReportSection {
  columnConfigurationTitle: string;
  columnsEndpoint?: () => Promise<ExportColumn[]>;
  key: string;
  reportType: ReportType;
  title: string;
}

/**
 * Section keys are stable and independent of ReportType/distinct-vs-not, matching BulkTargetIncludedReports'
 * field names, so checkbox identity on the Configure Report step never changes when isDistinct toggles.
 *
 * `title` (Configure Report step checkbox label) never mentions "Distinct" and never changes.
 * `columnConfigurationTitle` (Column Configuration step section heading) DOES vary for the Providers sections
 * when isDistinct is on, mirroring the Terminations wizard's getReportSections(), which also flips its section
 * title to "Distinct Service Area/Opportunity Providers" on that step only - only the resolved
 * reportType/columnsEndpoint additionally change alongside it.
 */
export const getTargetingReportSections = (isDistinct: boolean, projectType: BulkProjectTypeEnum): TargetingReportSection[] => {
  const exportService = new ExportService();
  const isZip = projectType === BulkProjectTypeEnum.Zip;

  const sections: TargetingReportSection[] = [
    {
      columnConfigurationTitle: "Project Details Target Overview",
      columnsEndpoint: () => exportService.getAdequacyBulkTargetingProjectDetailsColumns(projectType),
      key: "volumeTargetProjectDetails",
      reportType: ReportType.AdequacyBulkVolumeTargetProjectDetails,
      title: "Project Details Target Overview",
    },
    {
      columnConfigurationTitle: "Service Area Changed Gaps",
      // Zip projects generate the zip "Specialty Summary by Zip Code" columns, so fetch those columns and use the
      // zip report type to keep zip presets separate from county presets.
      columnsEndpoint: isZip ? exportService.getAdequacyBulkTargetingZipServiceAreaChangedGapsColumns : exportService.getAdequacyBulkTargetingChangedGapsColumns,
      key: "serviceAreaChangedGaps",
      reportType: isZip ? ReportType.ZipAdequacyBulkTargetChangedGaps : ReportType.AdequacyBulkTargetChangedGaps,
      title: "Service Area Changed Gaps",
    },
    {
      columnConfigurationTitle: "Service Area Specialty Summary Comparison",
      // Zip projects generate the zip "Specialty Summary by Zip Code" columns, so fetch those columns and use the
      // zip report type to keep zip presets separate from county presets.
      columnsEndpoint: isZip ? exportService.getAdequacyBulkTargetingZipServiceAreaSpecialtySummaryComparisonColumns : exportService.getAdequacyBulkTargetingSpecialtySummaryComparisonsColumns,
      key: "serviceAreaSpecialtySummaryComparison",
      reportType: isZip ? ReportType.ZipAdequacyBulkTargetSpecialtySummaryComparison : ReportType.AdequacyBulkTargetSpecialtySummaryComparison,
      title: "Service Area Specialty Summary Comparison",
    },
  ];

  if (!isZip) {
    sections.push(
      {
        columnConfigurationTitle: "Opportunity Changed Gaps",
        columnsEndpoint: exportService.getAdequacyBulkTargetingChangedGapsColumns,
        key: "opportunityChangedGaps",
        reportType: ReportType.AdequacyBulkTargetChangedGaps,
        title: "Opportunity Changed Gaps",
      },
      {
        columnConfigurationTitle: "Opportunity Specialty Summary Comparison",
        columnsEndpoint: exportService.getAdequacyBulkTargetingSpecialtySummaryComparisonsColumns,
        key: "opportunitySpecialtySummaryComparison",
        reportType: ReportType.AdequacyBulkTargetSpecialtySummaryComparison,
        title: "Opportunity Specialty Summary Comparison",
      },
    );
  }

  sections.push({
    columnConfigurationTitle: "Target Upload File",
    key: "targetUploadFile",
    reportType: ReportType.AdequacyBulkTargetUploadFile,
    title: "Target Upload File",
  });

  // Zip projects never offer a distinct-providers option, matching the previous availableReportTypes Zip branch.
  const useDistinctProviders = isDistinct && !isZip;

  sections.push({
    columnConfigurationTitle: useDistinctProviders ? "Distinct Service Area Providers" : "Service Area Providers",
    columnsEndpoint: useDistinctProviders
      ? () => exportService.getAdequacyBulkTargetingDistinctProvidersColumns(projectType)
      : () => exportService.getAdequacyBulkTargetingProvidersColumns(projectType),
    key: "serviceAreaProviders",
    reportType: useDistinctProviders ? ReportType.AdequacyBulkTargetDistinctProviders : ReportType.AdequacyBulkTargetProviders,
    title: "Service Area Providers",
  });

  if (!isZip) {
    sections.push({
      columnConfigurationTitle: useDistinctProviders ? "Distinct Opportunity Providers" : "Opportunity Providers",
      columnsEndpoint: useDistinctProviders
        ? () => exportService.getAdequacyBulkTargetingDistinctProvidersColumns(projectType)
        : () => exportService.getAdequacyBulkTargetingProvidersColumns(projectType),
      key: "opportunityProviders",
      reportType: useDistinctProviders ? ReportType.AdequacyBulkTargetDistinctProviders : ReportType.AdequacyBulkTargetProviders,
      title: "Opportunity Providers",
    });
  }

  return sections;
};
